import { useEffect, type ReactNode } from 'react'
import { Box, Button, Typography } from '@mui/material'

import { RecordingControl } from '@/apps/latest/components/organisms/MeetingBlock/RecordingControl'
import type { ViewStatus } from '@/apps/latest/shared/recording-status/types'
import { NOTION_BUTTON_TEXT_SX } from '@/shared/styles/buttonStyles'
import type { NutritionCustomer } from './api'
import { deriveAge } from './NutritionReportPanel'

// 録音の操作。録る前・録っている間と、送った後に録り直せる段階 (作る前・失敗) で渡す。
interface NutritionRecordingControl {
  status: ViewStatus
  // 録音中の音量で mic の輪を脈動させる (engine.stream)
  stream: MediaStream | null
  elapsedSeconds: number
  onStart: () => void
  onStop: () => void
  // 送った後の録り直し。mic でなく「録り直す」の文字で出す (録るのが本線に見えないように)。
  // 今の音声を捨てる手なので、開始の確認は page が挟む (onStart が開く)。
  restart?: boolean
  // 録り直しの往復中 (捨てて、録音イベントを取り直すまで)。釦を止め、二度目の確認を開かせない
  busy?: boolean
}

interface Props {
  // カルテのお客様 (マスタ)。取得中は undefined
  customer?: NutritionCustomer
  // 年齢を数える日 (記録の実施日。今回がまだ無ければ今日)
  ageOn?: string
  recording?: NutritionRecordingControl
  // 名前の脇に出す状態の 1 行 (「文字起こし中…」など)。段階は page が決める。
  caption?: string | null
  // 録音の操作の左に置く pill (担当。薬剤師の PatientInfoBar の「mic の左に pill 群」と同じ並び)。
  pillSlot?: ReactNode
  // 右端の手 (「作る」「もう一度文字起こす」)。
  rightSlot?: ReactNode
}

const formatElapsed = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0')
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`
}

// カルテの名前の行 = お客様の 1 行 + 今回の操作 (PatientInfoBar の文法)。名前 (大) の脇に 年齢・性別・状態を
// baseline で並べる。回目は出さない (回の属性で、数えるのは回の選択 1 か所。design-book ADR-0084)。
// 右端は 録る前と録っている間が 経過時間 + mic / ■、その後は段階の手 (+ 録り直す)。
// 一時停止・破棄は持たない (batch だけ。相談は止めずに 1 本で録る)。
export const NutritionRecordingBar = ({
  customer,
  ageOn,
  recording,
  caption,
  pillSlot,
  rightSlot,
}: Props) => {
  // 年齢は実施日 (開始の日。back の 1 定義) で数える
  const age = deriveAge(customer?.birth_date ?? '', ageOn)
  const meta = [age && `${age}歳`, customer?.sex].filter(Boolean).join(' ')

  useRecordingShortcut(recording)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: { xs: 1, sm: 2 },
        rowGap: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 0.75,
        }}
      >
        <Typography
          variant="h6"
          component="h1"
          sx={{ fontWeight: 600, lineHeight: 1.3 }}
        >
          {customer?.name ?? ''}
        </Typography>
        {meta && (
          <Typography variant="caption" color="text.secondary">
            {meta}
          </Typography>
        )}
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {pillSlot}
      {recording && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {recording.status === 'recording' && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
              aria-label="経過時間"
            >
              {formatElapsed(recording.elapsedSeconds)}
            </Typography>
          )}
          {recording.restart && recording.status !== 'recording' ? (
            <Button
              variant="text"
              size="small"
              sx={NOTION_BUTTON_TEXT_SX}
              onClick={recording.onStart}
              disabled={recording.busy}
            >
              録り直す
            </Button>
          ) : (
            <RecordingControl
              status={recording.status}
              stream={recording.stream}
              onStart={recording.onStart}
              onStop={recording.onStop}
            />
          )}
        </Box>
      )}
      {rightSlot}
    </Box>
  )
}

// ⇧Enter で録音の開始 / 停止 (RecordingControl の tooltip が約束している手元操作。薬剤師と同じ)。
// 入力欄 (メモ) に focus 中は改行を優先する。
const useRecordingShortcut = (recording?: NutritionRecordingControl) => {
  useEffect(() => {
    if (!recording) return
    const handler = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.key !== 'Enter' || e.repeat) return
      const t = e.target as HTMLElement | null
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      )
        return
      if (recording.status === 'idle') {
        e.preventDefault()
        recording.onStart()
      } else if (recording.status === 'recording') {
        e.preventDefault()
        recording.onStop()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [recording])
}
